import { ATTRIBUTION_MODEL_FIELDS, attributionModelFieldsToQueryDimensions, isLinearQueryFilters, isOttAudioOrDisplayQueryFilters, isOverlapQueryFilters, linearModelFiltersToQueryFilters, modelFiltersToQueryFilters, overlapModelFiltersToQueryFilters, getFootfallIncompatibility, resolveAttributionReportTypes } from "../../../../models/looker/modules/attribution";
import { FilterTypes } from "../../../../types";
import { REPORT_TYPE_TO_QUERY } from "./query";
export const buildAttributionQuery = (filters, options = {}) => {
    const fields = attributionModelFieldsToQueryDimensions(filters.fields || []).sort();
    if (fields.length === 0) {
        throw new Error("You must specify at least one field to query");
    }
    if (!filters.where || filters.where.length === 0) {
        throw new Error("You must specify at least one filter in the where clause");
    }
    const requestedFields = filters.fields || [];
    const footfallIncompatibility = getFootfallIncompatibility(requestedFields);
    if (footfallIncompatibility) {
        const offenders = footfallIncompatibility.fields.join(", ");
        throw new Error(footfallIncompatibility.reason === "incompatible-with-footfall"
            ? `Cannot query footfall fields with incompatible fields: ${offenders}`
            : `Cannot query conversion time fields with impression time footfall fields: ${offenders}`);
    }
    const where = [...filters.where];
    // Shared with the reporting API request builder so both transports refuse the
    // same queries. The filter may carry a single ReportType (EQ) or a
    // ReportType[] (IN) depending on the frontend's channel mode.
    const reportTypes = resolveAttributionReportTypes(where);
    // Hand the parsers the canonical list rather than the raw filter. They would
    // otherwise re-derive flt_media_type from the original value, so a repeated
    // selection would reach Looker as "VIDEO,VIDEO" here and as "VIDEO" through
    // the endpoint — one input, two queries, two cache keys.
    const reportTypeIndex = where.findIndex((filter) => filter.field === "reportType");
    if (reportTypeIndex !== -1) {
        where[reportTypeIndex] = {
            type: FilterTypes.IN,
            field: "reportType",
            value: reportTypes
        };
    }
    // If pixelIds filter is not provided by the UI, this means that there are either no pixels, or the user has explicitly chosen to not filter by any pixels. Looker includes all pixels by default; therefore, we need to add an empty pixelIds filter (set to NULL by the filter parser code) to ensure the query behaves as expected in both cases.
    if (!where.find((filter) => filter.field === "pixelIds")?.value) {
        where.push({
            type: FilterTypes.IN,
            field: "pixelIds",
            value: []
        });
    }
    // Route to the correct Looker explore by inspecting the full set of selected
    // channels. OVERLAP and LINEAR each have a dedicated explore; all digital
    // channels (VIDEO, AUDIO, DISPLAY) share explore_conversion and rely on
    // flt_media_type for per-channel filtering within that explore.
    const queryFilters = reportTypes.some((rt) => rt === "OVERLAP")
        ? overlapModelFiltersToQueryFilters(where)
        : reportTypes.some((rt) => rt === "LINEAR")
            ? linearModelFiltersToQueryFilters(where)
            : modelFiltersToQueryFilters(where);
    const queryTemplate = reportTypes.some((rt) => rt === "OVERLAP")
        ? REPORT_TYPE_TO_QUERY["OVERLAP"]
        : reportTypes.some((rt) => rt === "LINEAR")
            ? REPORT_TYPE_TO_QUERY["LINEAR"]
            : REPORT_TYPE_TO_QUERY["VIDEO"];
    const { isBlacklistEnabled, isAdvertiserAttrEnabled, isUniversalPixelConversionEventsEnabled } = options;
    if (isBlacklistEnabled) {
        if (isOttAudioOrDisplayQueryFilters(queryFilters)) {
            queryFilters["explore_conversion.pmt_filter_out_fox_anomalous_ips"] = "yes";
        }
        if (isLinearQueryFilters(queryFilters)) {
            queryFilters["explore_linear_conversion.pmt_filter_out_fox_anomalous_ips"] = "yes";
        }
        if (isOverlapQueryFilters(queryFilters)) {
            queryFilters["explore_overlap_conversion.pmt_filter_out_fox_anomalous_ips"] = "yes";
        }
    }
    if (isAdvertiserAttrEnabled && isOttAudioOrDisplayQueryFilters(queryFilters)) {
        queryFilters["explore_conversion.pmt_advertiser_attr"] = "yes";
    }
    if (isUniversalPixelConversionEventsEnabled && isOttAudioOrDisplayQueryFilters(queryFilters)) {
        const pixelType = where.find((filter) => filter.field === "pixelType")?.value;
        if (pixelType === "UNIVERSAL") {
            queryFilters["explore_conversion.pmt_up_total_conversions"] = "yes";
        }
    }
    // Serve static-web attribution from the expanded table (att_web_expanded_91day)
    // when the Insights Dashboard testing toggle is on. Gated on the STATIC pixel
    // path only, mirroring the explore_conversion parameter added in
    // looker-enterprise#505. Driven by explicit UI intent (useExpandedTable filter),
    // so no feature flag is read here — the toggle is only surfaced when its flag is on.
    const useExpandedTable = where.find((filter) => filter.field === "useExpandedTable")?.value;
    if (useExpandedTable && isOttAudioOrDisplayQueryFilters(queryFilters)) {
        const pixelType = where.find((filter) => filter.field === "pixelType")?.value;
        if (pixelType === "STATIC") {
            queryFilters["explore_conversion.pmt_use_expanded_table"] = "yes";
        }
    }
    // Apply impression threshold filter for LINEAR reports at the query level.
    // The threshold is a data quality gate on each dimension bucket — Linear TV
    // impressions are sampled data and the fractional multi-touch attribution math
    // becomes statistically unreliable below a minimum sample size. Apply it
    // unconditionally whenever any queried dimension declares a threshold, so the
    // row set is consistent regardless of which metric was selected.
    if (reportTypes.some((rt) => rt === "LINEAR") && isLinearQueryFilters(queryFilters)) {
        let highestThreshold;
        for (const field of requestedFields) {
            const fieldConfig = ATTRIBUTION_MODEL_FIELDS[field];
            if (fieldConfig.type === "dimension" && fieldConfig.impressionThreshold !== undefined) {
                if (highestThreshold === undefined || fieldConfig.impressionThreshold > highestThreshold) {
                    highestThreshold = fieldConfig.impressionThreshold;
                }
            }
        }
        if (highestThreshold !== undefined) {
            queryFilters["explore_linear_conversion.projected_impressions"] = `>=${highestThreshold}`;
        }
    }
    return {
        ...queryTemplate,
        limit: "-1",
        filters: queryFilters,
        fields: fields
    };
};
//# sourceMappingURL=utils.js.map